import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { UserFileRead } from '@/uppy-file-upload/schemas'

// ============================================================================
// Types
// ============================================================================

export type FileListRequestStatus = 'idle' | 'loading' | 'success' | 'error'

export interface FileListRequestState {
  status: FileListRequestStatus
  error: string | null
  statusCode?: number
}

/**
 * Root state for files feature
 */
export interface UserFilesState {
  // Single source of truth for file records, keyed by file id
  filesById: Record<string, UserFileRead>

  // Ordered file ids per folder. References into `filesById` - holds no records itself
  filesByFolderId: Record<string, string[]>

  // File-list request state per folder
  fileListRequestsByFolderId: Record<string, FileListRequestState>
}

// ============================================================================
// Initial State
// ============================================================================

const initialState: UserFilesState = {
  filesById: {},
  filesByFolderId: {},
  fileListRequestsByFolderId: {},
}

// ============================================================================
// Slice
// ============================================================================

export const userFilesSlice = createSlice({
  name: 'userFilesState',
  initialState,
  reducers: {
    // ============== Listener Triggers ==============

    fetchFilesForFolder: (_state, _action: PayloadAction<{ folderId: string }>) => {
      // Handled by listener middleware
    },

    // ============== Request State ==============

    fetchFilesStarted: (state, action: PayloadAction<{ folderId: string }>) => {
      state.fileListRequestsByFolderId[action.payload.folderId] = {
        status: 'loading',
        error: null,
      }
    },

    fetchFilesSucceeded: (state, action: PayloadAction<{ folderId: string; files: UserFileRead[] }>) => {
      const { folderId, files } = action.payload
      // Store the records by id - the folder keeps only their ids, in order
      for (const file of files) {
        state.filesById[file.id] = file
      }
      state.filesByFolderId[folderId] = files.map((file) => file.id)
      state.fileListRequestsByFolderId[folderId] = {
        status: 'success',
        error: null,
      }
    },

    fetchFilesFailed: (state, action: PayloadAction<{ folderId: string; error: string; statusCode?: number }>) => {
      state.fileListRequestsByFolderId[action.payload.folderId] = {
        status: 'error',
        error: action.payload.error,
        statusCode: action.payload.statusCode,
      }
    },

    /**
     * Insert or update file records in the store. The single way to write records,
     * used by the by-id fetch, prefetch, polling status refresh, and rename.
     */
    upsertUserFiles: (state, action: PayloadAction<UserFileRead[]>) => {
      for (const file of action.payload) {
        state.filesById[file.id] = file
      }
    },

    clearFolderFiles: (state, action: PayloadAction<{ folderId: string }>) => {
      const fileIds = state.filesByFolderId[action.payload.folderId] ?? []
      for (const fileId of fileIds) {
        delete state.filesById[fileId]
      }

      delete state.filesByFolderId[action.payload.folderId]
      delete state.fileListRequestsByFolderId[action.payload.folderId]
    },

    clearMatterFiles: (state, action: PayloadAction<{ matterId: string; folderIds?: string[] }>) => {
      const idsToRemove = new Set<string>()
      for (const file of Object.values(state.filesById)) {
        if (file.matter_id === action.payload.matterId) {
          idsToRemove.add(file.id)
        }
      }

      for (const fileId of idsToRemove) {
        delete state.filesById[fileId]
      }

      for (const [folderId, fileIds] of Object.entries(state.filesByFolderId)) {
        if (action.payload.folderIds?.includes(folderId)) {
          delete state.filesByFolderId[folderId]
          continue
        }

        const nextFileIds = fileIds.filter((fileId) => !idsToRemove.has(fileId))
        if (nextFileIds.length === 0) {
          delete state.filesByFolderId[folderId]
        } else {
          state.filesByFolderId[folderId] = nextFileIds
        }
      }

      action.payload.folderIds?.forEach((folderId) => {
        delete state.fileListRequestsByFolderId[folderId]
      })
    },

    /**
     * Reset to initial state
     */
    reset: () => initialState,
  },
})

// ============================================================================
// Exports
// ============================================================================

export const UserFilesActions = userFilesSlice.actions
export default userFilesSlice.reducer
